/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from 'react';
import { useGetVisitorId } from '../../api/contact';
import './MessageCenterPage.scss';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { useTitle } from '../../hooks/useTitle.ts';

export const MessageCenterPage = () => {
  useTitle('Message Center - Immersive Homes');
  const { data: user, isPending: userLoading } = useGetCurrentUser();
  const { data: visitorData, isPending: visitorLoading } = useGetVisitorId(
    user?.userId || ''
  );
  const trackingCode = import.meta.env
    .VITE_HUBSPOT_CHAT_TRACKING_CODE as string;
  const [scriptBlocked, setScriptBlocked] = useState(false);

  useEffect(() => {
    if (userLoading || visitorLoading) return;

    if (!user?.userId || !visitorData) return;

    const onConversationsAPIReady = () => {
      (window as any).HubSpotConversations.widget.load();
    };

    try {
      (window as any).hsConversationsSettings = {
        loadImmediately: false,
        avoidInlineStyles: true,
        inlineEmbedSelector: '#hubspot-messages',
        identificationEmail: visitorData.email,
        identificationToken: visitorData.token,
      };

      if ((window as any).HubSpotConversations) {
        onConversationsAPIReady();
      } else {
        (window as any).hsConversationsOnReady = [onConversationsAPIReady];
      }

      if (!document.getElementById('hubspot-tracking-code')) {
        const script = document.createElement('script');
        script.src = `//js-na1.hs-scripts.com/${trackingCode}.js`;
        script.async = true;
        script.id = 'hubspot-tracking-code';

        script.onerror = () => {
          setScriptBlocked(true);
        };

        const timeout = setTimeout(() => {
          if (!(window as any).HubSpotConversations) {
            setScriptBlocked(true);
          }
        }, 5000);

        document.body.appendChild(script);

        return () => {
          clearTimeout(timeout);
        };
      }
    } catch (err) {
      console.error('Failed to initialize HubSpot chat widget:', err);
      setScriptBlocked(true);
    }

    return () => {
      // Cleanup function to remove the widget when the component unmounts
      if (
        (window as any).HubSpotConversations &&
        (window as any).HubSpotConversations.widget
      ) {
        (window as any).HubSpotConversations.widget.refresh();
      }
    };
  }, [user, visitorData, trackingCode, userLoading, visitorLoading]);

  if (userLoading || visitorLoading) {
    return (
      <Box className="hubspot-chat">
        <Typography variant="h1">Message Center</Typography>
        <Box className="loading">
          <CircularProgress sx={{ marginRight: '12px' }} />
          <Typography>Loading Messages...</Typography>
        </Box>
      </Box>
    );
  }

  if (!user?.userId) {
    return (
      <Box className="hubspot-chat">
        <Typography variant="h1">Message Center</Typography>
        <Box className="error">
          <Typography color="error">
            Please sign in to access the message center
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!visitorData) {
    return (
      <Box className="hubspot-chat">
        <Typography variant="h1">Message Center</Typography>
        <Box className="error">
          <Typography color="error">
            Unable to load message center. Please try again later.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="hubspot-chat">
      <Typography variant="h1">Message Center</Typography>
      {scriptBlocked && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Unable to load the message center. This may be due to:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>
              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Firefox's Enhanced Tracking Protection
              </a>{' '}
              being set to strict
            </li>
            <li>An ad blocker or privacy extension</li>
            <li>Network restrictions</li>
          </ul>
          <Typography variant="body2" sx={{ mt: 1 }}>
            To use the message center, please:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>Disable Enhanced Tracking Protection for this site</li>
            <li>Allow scripts from hubspot.com</li>
            <li>Or try using a different browser</li>
          </ul>
        </Alert>
      )}
      <Box id="hubspot-messages"></Box>
    </Box>
  );
};
